import type { EditorView, NodeView } from 'prosemirror-view'
import { NodeSelection } from 'prosemirror-state'
import type { CellReference } from '../schemas'
import { tableReferenceHref, tableReferenceLabel, type TableReferenceTarget } from '../reference'

export function referenceNodeView(
  id: string,
  number: number,
  reference: CellReference | undefined,
  view: EditorView,
  getPos: () => number | undefined,
  matterId: string | undefined,
  openReference: ((reference: TableReferenceTarget) => boolean) | null
): NodeView {
  const dom = document.createElement('span')
  dom.contentEditable = 'false'
  dom.className =
    'mx-0.5 inline-flex items-center gap-1 rounded border border-brand-neutral-300 bg-brand-neutral-200 px-1 text-xs text-brand-neutral-800 data-[active=true]:bg-brand-200 data-[active=true]:border-brand-400 data-[active=true]:text-brand-900'
  const label = reference ? tableReferenceLabel(reference) : 'Source unavailable'
  dom.title = `${label}${reference?.excerpt ? ` — ${reference.excerpt}` : ''}`
  const move = document.createElement('button')
  move.type = 'button'
  move.draggable = true
  move.textContent = '⠿'
  move.setAttribute('aria-label', `Move reference ${reference?.document_name ?? id}`)
  move.title = 'Drag to move, or use the left and right arrow keys'
  move.className = 'cursor-grab rounded px-0.5 active:cursor-grabbing hover:bg-brand-neutral-300 focus-visible:outline focus-visible:outline-brand-500'
  move.onkeydown = (event) => {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return
    event.preventDefault()
    const pos = getPos()
    if (!view.editable || pos === undefined) return
    const next = pos + (event.key === 'ArrowLeft' ? -1 : 1)
    if (next < 1 || next >= view.state.doc.content.size - 1) return
    const node = view.state.doc.nodeAt(pos)!
    const transaction = view.state.tr.delete(pos, pos + 1).insert(next, node)
    transaction.setSelection(NodeSelection.create(transaction.doc, next))
    view.dispatch(transaction)
    const element = view.nodeDOM(next)
    if (element instanceof HTMLElement) element.querySelector<HTMLButtonElement>('button')?.focus()
  }
  const href = reference && matterId ? tableReferenceHref(reference, matterId) : null
  const source = document.createElement(href ? 'a' : 'span')
  source.textContent = String(number)
  source.draggable = false
  source.setAttribute('aria-label', label)
  if (href) {
    source.className =
      'cursor-pointer rounded px-0.5 transition-colors hover:bg-brand-neutral-300 hover:text-brand-neutral-950 focus-visible:outline focus-visible:outline-brand-500'
    source.setAttribute('href', href)
    source.setAttribute('target', '_blank')
    source.setAttribute('rel', 'noopener noreferrer')
    source.onclick = (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      if (reference && openReference?.(reference)) event.preventDefault()
    }
  }
  const remove = document.createElement('button')
  remove.type = 'button'
  remove.textContent = '×'
  remove.className = 'rounded px-0.5 hover:bg-brand-neutral-300 hover:text-brand-neutral-950 focus-visible:outline focus-visible:outline-brand-500'
  remove.setAttribute('aria-label', `Remove reference ${reference?.document_name ?? id}`)
  remove.onmousedown = (event) => event.preventDefault()
  remove.onclick = () => {
    const pos = getPos()
    if (!view.editable || pos === undefined) return
    view.dispatch(view.state.tr.delete(pos, pos + 1))
    view.focus()
  }
  dom.append(move, source, remove)
  return {
    dom,
    stopEvent: (event) => {
      if (event.type.startsWith('drag') || event.type === 'drop') return false
      if (event.target === move && event.type === 'mousedown') return false
      return event.target instanceof Element && Boolean(event.target.closest('button, a'))
    },
    ignoreMutation: () => true,
  }
}
